// API Base URL - On production use /api (Vercel proxies to Render). No CORS: same origin.
const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
export const API_BASE_URL = isLocalhost
  ? (process.env.REACT_APP_API_URL || 'http://localhost:5000/api')
  : (typeof window !== 'undefined' ? `${window.location.origin}/api` : 'https://trempi.com/api');

// Default timeout for API calls (10 seconds)
const DEFAULT_TIMEOUT = 10000;

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Helper function to create a timeout promise
const createTimeoutPromise = (ms) => {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error('Request timed out. Please try again.')), ms);
  });
};

// Helper function for API calls with timeout
const apiCall = async (endpoint, options = {}, timeout = DEFAULT_TIMEOUT) => {
  try {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Create fetch promise
    const fetchPromise = fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    // Race between fetch and timeout
    const response = await Promise.race([
      fetchPromise,
      createTimeoutPromise(timeout)
    ]);

    const data = await response.json();

    if (!response.ok) {
      // Handle authentication errors
      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authAccount');
      }
      throw new Error(data.message || 'API request failed');
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// =======================
// AUTH APIs
// =======================

// Check if phone has an account (without sending OTP)
export const checkPhone = async (phone) => {
  return apiCall('/auth/check-phone', {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });
};

// Request OTP - for existing users just phone, for new users include registration data
// Use longer timeout (35 seconds) since SMS sending can take time with InforU
export const requestOTP = async (phone, name = null, email = null, gender = null, hidePhone = false) => {
  const body = { phone };
  if (name) body.name = name;
  if (email) body.email = email;
  if (gender) body.gender = gender;
  if (hidePhone) body.hide_phone = hidePhone;
  
  return apiCall('/auth/request-otp', {
    method: 'POST',
    body: JSON.stringify(body),
  }, 35000); // 35 second timeout for OTP requests (InforU can be slow)
};

// Verify OTP (login or register). For new users pass name, email, gender, hide_phone.
export const verifyOTP = async (phone, otp, name = null, email = null, gender = null, hidePhone = false) => {
  const body = { phone, otp };
  if (name) body.name = name;
  if (email) body.email = email;
  if (gender) body.gender = gender;
  if (hidePhone) body.hide_phone = true;
  
  return apiCall('/auth/verify-otp', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

// Get current user
export const getCurrentUser = async () => {
  return apiCall('/auth/me');
};

// Update account
export const updateAccount = async (data) => {
  return apiCall('/auth/account', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

// Logout
export const logout = async () => {
  try {
    await apiCall('/auth/logout', { method: 'POST' });
  } finally {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authAccount');
  }
};

// Get my offers
export const getMyOffers = async () => {
  return apiCall('/auth/my-offers');
};

// Get my requests
export const getMyRequests = async () => {
  return apiCall('/auth/my-requests');
};

// Get my joined rides (as passenger)
export const getMyJoinedRides = async () => {
  return apiCall('/auth/my-joined-rides');
};

// Event APIs
export const validateEventCode = async (eventCode) => {
  return apiCall('/validate-event-code', {
    method: 'POST',
    body: JSON.stringify({ event_code: eventCode }),
  });
};

export const createEvent = async (eventData) => {
  return apiCall('/admin/event', {
    method: 'POST',
    body: JSON.stringify(eventData),
  });
};

export const getEventStats = async (eventId) => {
  return apiCall(`/admin/event/${eventId}/stats`);
};

// Driver APIs
export const createCarpoolOffer = async (offerData) => {
  return apiCall('/carpool/offer', {
    method: 'POST',
    body: JSON.stringify(offerData),
  });
};

export const getCarpoolOffer = async (offerId) => {
  return apiCall(`/carpool/offer/${offerId}`);
};

export const updateCarpoolOffer = async (offerId, updates) => {
  return apiCall(`/carpool/offer/${offerId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
};

export const deleteCarpoolOffer = async (offerId) => {
  return apiCall(`/carpool/offer/${offerId}`, {
    method: 'DELETE',
  });
};

export const getOfferRequests = async (offerId) => {
  return apiCall(`/carpool/offer/${offerId}/requests`);
};

export const acceptRequest = async (offerId, requestId) => {
  return apiCall(`/carpool/offer/${offerId}/accept-request`, {
    method: 'POST',
    body: JSON.stringify({ request_id: requestId }),
  });
};

export const rejectRequest = async (offerId, requestId) => {
  return apiCall(`/carpool/offer/${offerId}/reject-request`, {
    method: 'POST',
    body: JSON.stringify({ request_id: requestId }),
  });
};

export const getPassengers = async (eventId, filters = {}) => {
  const query = new URLSearchParams(filters).toString();
  return apiCall(`/carpool/passengers?event_id=${eventId}&${query}`);
};

export const sendInvitation = async (offerId, requestId) => {
  return apiCall(`/carpool/offer/${offerId}/send-invitation`, {
    method: 'POST',
    body: JSON.stringify({ request_id: requestId }),
  });
};

// Passenger APIs
export const createCarpoolRequest = async (requestData) => {
  return apiCall('/carpool/request', {
    method: 'POST',
    body: JSON.stringify(requestData),
  });
};

export const getCarpoolRequest = async (requestId) => {
  return apiCall(`/carpool/request/${requestId}`);
};

export const updateCarpoolRequest = async (requestId, updates) => {
  return apiCall(`/carpool/request/${requestId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
};

export const deleteCarpoolRequest = async (requestId) => {
  return apiCall(`/carpool/request/${requestId}`, {
    method: 'DELETE',
  });
};

export const getPublicOffers = async (eventId, filters = {}) => {
  const query = new URLSearchParams({ event_id: eventId, ...filters }).toString();
  return apiCall(`/carpool/offers?${query}`);
};

export const sendJoinRequest = async (requestId, offerId) => {
  return apiCall(`/carpool/request/${requestId}/join-request`, {
    method: 'POST',
    body: JSON.stringify({ offer_id: offerId }),
  });
};

export const getInvitations = async (requestId) => {
  return apiCall(`/carpool/request/${requestId}/invitations`);
};

export const acceptInvitation = async (requestId, matchId) => {
  return apiCall(`/carpool/request/${requestId}/accept-invitation`, {
    method: 'POST',
    body: JSON.stringify({ match_id: matchId }),
  });
};

export const rejectInvitation = async (requestId, matchId) => {
  return apiCall(`/carpool/request/${requestId}/reject-invitation`, {
    method: 'POST',
    body: JSON.stringify({ match_id: matchId }),
  });
};

// Match APIs
export const getMatch = async (matchId) => {
  return apiCall(`/carpool/match/${matchId}`);
};

export const deleteMatch = async (matchId) => {
  return apiCall(`/carpool/match/${matchId}`, {
    method: 'DELETE',
  });
};

// Admin APIs
export const getDrivers = async (eventId, filters = {}, page = 1) => {
  const query = new URLSearchParams({ ...filters, page }).toString();
  return apiCall(`/admin/event/${eventId}/drivers?${query}`);
};

export const getPassengersAdmin = async (eventId, filters = {}, page = 1) => {
  const query = new URLSearchParams({ ...filters, page }).toString();
  return apiCall(`/admin/event/${eventId}/passengers?${query}`);
};

export const getMatches = async (eventId) => {
  return apiCall(`/admin/event/${eventId}/matches`);
};

export const suggestMatch = async (eventId, driverId, passengerId, message) => {
  return apiCall(`/admin/event/${eventId}/suggest-match`, {
    method: 'POST',
    body: JSON.stringify({ driver_id: driverId, passenger_id: passengerId, message }),
  });
};

export const exportData = async (eventId, type, format) => {
  const query = new URLSearchParams({ type, format }).toString();
  return apiCall(`/admin/event/${eventId}/export?${query}`);
};

export const broadcastMessage = async (eventId, recipientType, subject, message) => {
  return apiCall(`/admin/event/${eventId}/broadcast`, {
    method: 'POST',
    body: JSON.stringify({ recipient_type: recipientType, subject, message }),
  });
};

export const sendReminders = async (eventId) => {
  return apiCall(`/admin/event/${eventId}/send-reminders`, {
    method: 'POST',
  });
};

// Notification APIs
export const getNotifications = async (userId) => {
  return apiCall(`/notifications/${userId}`);
};

export const markNotificationRead = async (notificationId) => {
  return apiCall(`/notifications/${notificationId}/read`, {
    method: 'PUT',
  });
};

const api = {
  // Auth
  requestOTP,
  verifyOTP,
  getCurrentUser,
  updateAccount,
  logout,
  getMyOffers,
  getMyRequests,
  // Event
  validateEventCode,
  createEvent,
  getEventStats,
  // Driver
  createCarpoolOffer,
  getCarpoolOffer,
  updateCarpoolOffer,
  deleteCarpoolOffer,
  getOfferRequests,
  acceptRequest,
  rejectRequest,
  getPassengers,
  sendInvitation,
  // Passenger
  createCarpoolRequest,
  getCarpoolRequest,
  updateCarpoolRequest,
  deleteCarpoolRequest,
  getPublicOffers,
  sendJoinRequest,
  getInvitations,
  acceptInvitation,
  rejectInvitation,
  // Match
  getMatch,
  deleteMatch,
  // Admin
  getDrivers,
  getPassengersAdmin,
  getMatches,
  suggestMatch,
  exportData,
  broadcastMessage,
  sendReminders,
  // Notifications
  getNotifications,
  markNotificationRead,
};

export default api;
